import React, { useEffect, useState } from 'react';
import api from '../api';

// Aufnahmeantrag (Vereinsanmeldung) – Anzeige und Erfassung im Admin
// Status am Schwimmer: null = nicht erfasst, 'fehlt', 'beim_trainer', 'eingegangen'

const PFLICHT_TRAININGS = 2;
const heute = () => new Date().toLocaleDateString('sv-SE');
const fmtDatum = (d) => (d ? new Date(d).toLocaleDateString('de-DE') : '–');
const fmtZeit = (d) => (d ? new Date(d).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '–');
const WEG = { papier: 'auf Papier', email: 'per E-Mail' };

export function useAntragLink() {
  const [url, setUrl] = useState('');
  useEffect(() => {
    api.get('/api/admin/aufnahmeantrag/link').then(r => setUrl(r.data.url || '')).catch(() => {});
  }, []);
  return url;
}

// Kleines Kennzeichen für Listen
export function AntragBadge({ status, ueberfaellig, trainings }) {
  let cfg = null;
  if (status === 'fehlt' && ueberfaellig) cfg = { bg: '#c62828', color: '#fff', text: '⚠ Antrag überfällig' };
  else if (status === 'fehlt') cfg = { bg: '#fff3e0', color: '#e65100', text: `Antrag fehlt (${trainings ?? 0}/${PFLICHT_TRAININGS} Trainings)` };
  else if (status === 'beim_trainer') cfg = { bg: '#e3f2fd', color: '#1565c0', text: 'Antrag beim Trainer' };
  if (!cfg) return null;
  return (
    <span style={{ background: cfg.bg, color: cfg.color, padding: '1px 8px', borderRadius: 10,
      fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap', display: 'inline-block' }}>
      {cfg.text}
    </span>
  );
}

function EingangFormular({ onSpeichern, arbeitet, knopfText }) {
  const [datum, setDatum] = useState(heute());
  const [ueber, setUeber] = useState('papier');
  return (
    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '0.6rem' }}>
      <div>
        <label className="form-label" style={{ fontSize: '0.72rem' }}>Eingegangen am</label>
        <input type="date" className="form-control" style={{ padding: '4px 8px' }} value={datum} max={heute()}
          onChange={e => setDatum(e.target.value)} />
      </div>
      <div>
        <label className="form-label" style={{ fontSize: '0.72rem' }}>Weg</label>
        <select className="form-control" style={{ padding: '4px 8px' }} value={ueber} onChange={e => setUeber(e.target.value)}>
          <option value="papier">Papier (über Trainer)</option>
          <option value="email">E-Mail an Schwimmadmin</option>
        </select>
      </div>
      <button className="btn btn-primary btn-sm" disabled={arbeitet || !datum} onClick={() => onSpeichern(datum, ueber)}>
        {knopfText}
      </button>
    </div>
  );
}

const rahmen = (farbe, hintergrund) => ({
  border: `1px solid ${farbe}`, background: hintergrund, borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem',
});

// Abschnitt im Wartelisten-Detailfenster (Eingang schon vor der Aufnahme, z.B. per E-Mail)
export function AntragWarteliste({ eintrag, darfBearbeiten, onGeaendert }) {
  const url = useAntragLink();
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState('');

  const setzen = async (daten) => {
    setArbeitet(true); setFehler('');
    try {
      const r = await api.put(`/api/admin/warteliste/${eintrag.id}/aufnahmeantrag`, daten);
      onGeaendert(r.data);
    } catch (err) {
      setFehler(err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally { setArbeitet(false); }
  };

  const da = !!eintrag.aufnahmeantrag_eingang_am;
  return (
    <>
      <div className="detail-section">Aufnahmeantrag</div>
      <div style={da ? rahmen('#c8e6c9', '#f1f8f1') : rahmen('var(--border)', '#f8f9fa')}>
        {da ? (
          <div style={{ fontSize: '0.88rem' }}>
            <strong style={{ color: '#2e7d32' }}>✓ Eingegangen</strong> am {fmtDatum(eintrag.aufnahmeantrag_eingang_am)} {WEG[eintrag.aufnahmeantrag_eingang_ueber] || ''}
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Wird bei der Aufnahme in die Gruppe übernommen.</div>
          </div>
        ) : (
          <div style={{ fontSize: '0.88rem' }}>
            Noch nicht eingegangen. Ohne Antrag ist nur eine <strong>vorläufige</strong> Aufnahme möglich
            (Abgabe spätestens nach {PFLICHT_TRAININGS} besuchten Trainings).
          </div>
        )}
        {url && (
          <div style={{ fontSize: '0.78rem', marginTop: 4 }}>
            <a href={url} target="_blank" rel="noopener noreferrer">📄 Aufnahmeantrag (PDF) öffnen</a>
          </div>
        )}
        {darfBearbeiten && !da && (
          <EingangFormular arbeitet={arbeitet} knopfText="✓ Eingang erfassen"
            onSpeichern={(datum, ueber) => setzen({ eingegangen: true, eingang_am: datum, ueber })} />
        )}
        {darfBearbeiten && da && (
          <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.5rem' }} disabled={arbeitet}
            onClick={() => window.confirm('Eingang des Aufnahmeantrags zurücksetzen?') && setzen({ eingegangen: false })}>
            Eingang zurücksetzen
          </button>
        )}
        {fehler && <div className="alert alert-error" style={{ marginTop: '0.5rem', marginBottom: 0 }}>{fehler}</div>}
      </div>
    </>
  );
}

// Karte im Schwimmer-Detail
export function AntragSchwimmer({ sw, darfBearbeiten, onGeaendert }) {
  const url = useAntragLink();
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState('');

  const setzen = async (daten, frage) => {
    if (frage && !window.confirm(frage)) return;
    setArbeitet(true); setFehler('');
    try {
      await api.put(`/api/admin/schwimmer/${sw.id}/aufnahmeantrag`, daten);
      onGeaendert();
    } catch (err) {
      setFehler(err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally { setArbeitet(false); }
  };

  const status = sw.aufnahmeantrag_status;
  const ueberfaellig = sw.aufnahmeantrag_ueberfaellig;
  const farbe = status === 'eingegangen' ? ['#c8e6c9', '#f1f8f1']
    : ueberfaellig ? ['#ef9a9a', '#fff5f5']
    : status ? ['#ffe0b2', '#fffaf2'] : ['var(--border)', '#fafafa'];

  return (
    <div className="card" style={{ marginBottom: '1.25rem' }}>
      <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: '0.95rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span>Aufnahmeantrag (Vereinsanmeldung)</span>
        {url && <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8rem', fontWeight: 400 }}>📄 PDF öffnen</a>}
      </div>
      <div style={{ padding: '1rem' }}>
        <div style={rahmen(farbe[0], farbe[1])}>
          {status === 'eingegangen' && (
            <div style={{ fontSize: '0.9rem' }}>
              <strong style={{ color: '#2e7d32' }}>✓ Liegt vor</strong> – eingegangen am {fmtDatum(sw.aufnahmeantrag_eingang_am)} {WEG[sw.aufnahmeantrag_eingang_ueber] || ''}
              {sw.aufnahmeantrag_erfasst_von_name && <span style={{ color: 'var(--text-muted)' }}> · erfasst von {sw.aufnahmeantrag_erfasst_von_name}</span>}
            </div>
          )}
          {status === 'beim_trainer' && (
            <div style={{ fontSize: '0.9rem' }}>
              <strong style={{ color: '#1565c0' }}>Beim Trainer abgegeben</strong> am {fmtZeit(sw.aufnahmeantrag_trainer_am)}
              {sw.aufnahmeantrag_trainer_name && ` (${sw.aufnahmeantrag_trainer_name})`}.
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Bitte Eingang bestätigen, sobald das Papier beim Schwimmadmin ist.</div>
            </div>
          )}
          {status === 'fehlt' && (
            <div style={{ fontSize: '0.9rem' }}>
              <strong style={{ color: ueberfaellig ? '#c62828' : '#e65100' }}>
                {ueberfaellig ? '⚠ Überfällig' : 'Fehlt'}
              </strong>{' '}– vorläufig aufgenommen am {fmtDatum(sw.aufnahmeantrag_fehlt_seit)},
              seitdem {sw.aufnahmeantrag_trainings ?? 0} von {PFLICHT_TRAININGS} Trainings besucht.
            </div>
          )}
          {!status && (
            <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              Nicht erfasst (Mitglied vor Einführung der Prüfung). Es werden keine Hinweise angezeigt.
            </div>
          )}

          {darfBearbeiten && status !== 'eingegangen' && (
            <EingangFormular arbeitet={arbeitet}
              knopfText={status === 'beim_trainer' ? '✓ Eingang bestätigen' : '✓ Eingang erfassen'}
              onSpeichern={(datum, ueber) => setzen({ status: 'eingegangen', eingang_am: datum, ueber })} />
          )}
          {darfBearbeiten && (
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
              {status !== 'fehlt' && (
                <button className="btn btn-secondary btn-sm" disabled={arbeitet}
                  onClick={() => setzen({ status: 'fehlt' }, 'Aufnahmeantrag als fehlend markieren?\n\nIn der Trainer-App erscheint dann ein Hinweis.')}>
                  Als fehlend markieren
                </button>
              )}
              {status && (
                <button className="btn btn-secondary btn-sm" disabled={arbeitet}
                  onClick={() => setzen({ status: null }, 'Aufnahmeantrag auf „nicht erfasst" setzen?\n\nNur für Mitglieder, die schon vor Einführung der Prüfung im Verein waren.')}>
                  Nicht erfasst (Altbestand)
                </button>
              )}
            </div>
          )}
          {fehler && <div className="alert alert-error" style={{ marginTop: '0.5rem', marginBottom: 0 }}>{fehler}</div>}
        </div>
      </div>
    </div>
  );
}
